import { useEffect, useState, useRef } from "react";
import {
  Box, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, FormErrorMessage,
  useDisclosure, Checkbox
} from "@chakra-ui/react";
import ContactDetailLogo from '../../../../../assets/images/ContactDetailLogo.svg';
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import { EmployeePersonalInfo } from "./modal";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Contact } from "./modal";
import { useAuth } from "../../../../../hooks/useAuth";

interface Props {
  employeeId: number | null;
  personalDetail?: EmployeePersonalInfo;
  reload: () => void;
}

export interface IContact {
  empId?: number | null;
  phone_Number: string;
  whatsApp_Number: string;
  us_Phone_Number: string;
  official_Email: string;
  personal_Email: string;
};


// 🔹 Mask Indian phone: 12345 67890
const maskPhone = (val: string) => {
  const digits = val.replace(/\D/g, "").slice(0, 10);
  if (digits.length > 5) {
    return digits.slice(0, 5) + " " + digits.slice(5);
  }
  return digits;
};

// 🔹 Mask US phone: (123) 456-7890
const maskUSPhone = (val: string) => {
  const digits = val.replace(/\D/g, "").slice(0, 10);
  if (digits.length > 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length > 3) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else if (digits.length > 0) {
    return `(${digits}`;
  }
  return digits;
};

// 🔹 Unmask before sending to backend
const unmask = (val: string) => val.replace(/\D/g, "");

const ContactDetails = ({ employeeId, personalDetail, reload }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { hasPermission, loginObj } = useAuth();
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [sameAsPhone, setSameAsPhone] = useState(false);
  const previousWhatsappRef = useRef<string>("");

  const loggedInEmployeeId = loginObj?.employeeId;

  const isOwnProfile = employeeId === loggedInEmployeeId;


  const ContactSchema: any = yup.object().shape({
    phone_Number: yup.string().required("Phone Number is required").test("len", "Phone number must be exactly 10 digits", (val) => unmask(val || "").length === 10)
      .test( "different-emergency", "Personal Phone Number cannot be the same as Emergency Contact Number",
        function (value) {  const phone = unmask(value || "");
          const emergency = unmask(personalDetail?.emergency_Contact_Number || "");
          return phone !== emergency;
        }
      ),

    whatsApp_Number: yup.string()
      .required("WhatsApp Number is required")
      .test("len", "whatsApp number must be exactly 10 digits", (val) => unmask(val || "").length === 10),

    us_Phone_Number: yup.string()
      .nullable()
      .test("us-phone", "US Phone Number must be valid (e.g., (123) 456-7890)", (val) => {
        if (!val) return true;
        return unmask(val).length === 10;
      }),
    official_Email: yup.string().email("Enter a valid Official Email").required("Official Email is required").test(
      "different-email", "Official Email and Personal Email cannot be the same",
      function (value) {
        return (!value || value.toLowerCase() !== (this.parent.personal_Email || "").toLowerCase()
        );
      }
    ),

    personal_Email: yup.string().email("Enter a valid Personal Email").required("Personal Email is required").test(
      "different-email", "Personal Email and Official Email cannot be the same",
      function (value) {
        return (!value || value.toLowerCase() !==
          (this.parent.official_Email || "").toLowerCase()
        );
      }
    ),
  });

  const { control, handleSubmit, formState: { errors, isSubmitting }, reset, setValue,
    getValues, } = useForm<IContact>({
      resolver: yupResolver(ContactSchema),
      defaultValues: {
        phone_Number: maskPhone(personalDetail?.phone_Number || ""),
        whatsApp_Number: maskPhone(personalDetail?.whatsApp_Number || ""),
        us_Phone_Number: maskUSPhone(personalDetail?.us_Phone_Number || ""),
        official_Email: personalDetail?.official_Email || "",
        personal_Email: personalDetail?.personal_Email || "",
      },
    });

  const phoneValue = useWatch({ control, name: "phone_Number", });
  const whatsappValue = useWatch({ control, name: "whatsApp_Number", });

  const showSameAsPhoneCheckbox = true;

  function handleEditClick() {
    const isSame =
      personalDetail?.phone_Number === personalDetail?.whatsApp_Number;
    previousWhatsappRef.current = maskPhone(
      personalDetail?.whatsApp_Number || ""
    );

    setSameAsPhone(!!isSame)
    reset({
      phone_Number: maskPhone(personalDetail?.phone_Number || ""),
      whatsApp_Number: maskPhone(personalDetail?.whatsApp_Number || ""),
      us_Phone_Number: maskUSPhone(personalDetail?.us_Phone_Number || ""),
      official_Email: personalDetail?.official_Email || "",
      personal_Email: personalDetail?.personal_Email || "",
    });
    onOpen();
  }

  const onSubmit = async (data: IContact) => {
    data.empId = employeeId;
    data.phone_Number = unmask(data.phone_Number);
    data.whatsApp_Number = unmask(data.whatsApp_Number);
    data.us_Phone_Number = unmask(data.us_Phone_Number);
    let endpoint = `Hrlense_Employee/personalDetail/${employeeId}`;
    const response = await fetchApi(endpoint, "PUT", data, null, "Contact Details Updated");
    if (response) {
      reload();
      onClose();
    }
  }

  useEffect(() => {
    if (!sameAsPhone) return;
    previousWhatsappRef.current = getValues("whatsApp_Number");
    setValue("whatsApp_Number", phoneValue || "", {
      shouldValidate: true,
    });
  }, [sameAsPhone]);

  return (
    <>
      <Box pt={2} mb={6} className="main">
        <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
          <Flex align="center" gap={3}>
            <Image src={ContactDetailLogo} alt={"Benifits & ID Numbers"} />
            <Text className="font-poppins text_sm text_medium col">
              Contact Details
            </Text>
          </Flex>
          {(hasPermission("Employee", "UpdateEmployeeContactDetail") || (hasPermission("Employee", "UpdateOwnContactDetail") && isOwnProfile)) &&
            (
              <IconButton aria-label="Edit" icon={<Image src={EditLogo} />} variant="none" onClick={() => handleEditClick()} />
            )}
        </Flex>

        <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
          {Contact.map((data, index) => {
            let value = personalDetail?.[data?.key] as string;
            if (data?.key === "phone_Number" && value) {
              value = maskPhone(value);
            } else if (data?.key === "us_Phone_Number" && value) {
              value = maskUSPhone(value);
            }
            return (
              <Grid key={index} templateColumns="1fr 1fr" alignItems="center" mb={2}>
                <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)"  >
                  {data?.label}
                </Text>
                <Text className="font-poppins text_sm text_medium col">
                  {value}
                </Text>
              </Grid>
            );
          })}
        </Box>
      </Box>

      {/* Drawer for editing */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Update Contact Details</DrawerHeader>
          <DrawerBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FormControl isInvalid={!!errors.phone_Number} isRequired mb={4}>
                <FormLabel> Personal Phone Number </FormLabel>
                <Controller
                  name="phone_Number" control={control}
                  render={({ field }) => (
                    <Input type="text" {...field}
                      value={maskPhone(field.value || "")}
                      onChange={(e) => field.onChange(maskPhone(e.target.value))}
                    />
                  )}
                />
                <FormErrorMessage>{errors.phone_Number?.message}</FormErrorMessage>
              </FormControl>


              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel mb={0}>WhatsApp Number</FormLabel>

                <Checkbox
                  size="sm"
                  isChecked={sameAsPhone}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSameAsPhone(checked);

                    if (!checked) {
                      setValue(
                        "whatsApp_Number",
                        previousWhatsappRef.current || "",
                        { shouldValidate: true }
                      );
                    }
                  }}
                >
                  Same as Phone Number
                </Checkbox>
              </Flex>

              <FormControl isInvalid={!!errors.whatsApp_Number} isRequired mb={4}>

                <Controller
                  name="whatsApp_Number" control={control}
                  render={({ field }) => (
                    <Input type="text" {...field}
                      isDisabled={sameAsPhone}
                      value={maskPhone(field.value || "")}
                      onChange={(e) => field.onChange(maskPhone(e.target.value))}
                    />
                  )}
                />
                <FormErrorMessage>{errors.whatsApp_Number?.message}</FormErrorMessage>
              </FormControl>


              <FormControl isInvalid={!!errors.us_Phone_Number} mb={4}>
                <FormLabel> US Phone </FormLabel>
                <Controller
                  name="us_Phone_Number" control={control}
                  render={({ field }) => (
                    <Input type="text" {...field}
                      value={maskUSPhone(field.value || "")}
                      onChange={(e) => field.onChange(maskUSPhone(e.target.value))}
                    />
                  )}
                />
                <FormErrorMessage>{errors.us_Phone_Number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.official_Email} isRequired mb={4}>
                <FormLabel> Official Email </FormLabel>
                <Controller
                  name="official_Email" control={control}
                  render={({ field }) => (
                    <Input type="string"  {...field} />
                  )}
                />
                <FormErrorMessage>{errors.official_Email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.personal_Email} isRequired mb={4}>
                <FormLabel> Personal Mail </FormLabel>
                <Controller
                  name="personal_Email" control={control}
                  render={({ field }) => (
                    <Input type="string"  {...field} />
                  )}
                />
                <FormErrorMessage>{errors.personal_Email?.message}</FormErrorMessage>
              </FormControl>

              <Box mt={6} textAlign="right">
                <Button type="submit" className="btn_theme btn_theme:hover" isLoading={isSubmitting}>
                  Update
                </Button>
              </Box>
            </form>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ContactDetails;
